// InMemoryBroker — JS port of the subset of controllerd needed for two
// browser tabs (facilitator + participant) on the same machine to
// drive a session through each other without a real controller.
//
// Mirrors the dispatch in cpp/controllerd/controller_ws_dispatch.cpp:
//
//   node.register     → mark connection registered; ack with protocolVersion
//   subscribe         → record topic patterns; ack accepted set
//   unsubscribe       → drop patterns; ack
//   state.get         → snapshot of one of: sessions, participants,
//                       nodes, events (others return an empty list)
//   session.start     → upsert session row, fan out
//   session.pause     → flip state, fan out
//   session.stop      → mark stopped, fan out
//   participant.claim → bind connection to (sessionId, participantRoleId),
//                       emit participant.status, ack with participant ref
//   participant.release → undo a claim, emit participant.status
//   participant.assign → admin path (facilitator binds an agent), not used
//                         in the shim today but accepted for parity
//   anything else     → fan out via subscriptions + `to.scope`
//
// Capability gates from the real controller are intentionally dropped:
// every same-origin connection is trusted, since spawning a tab on the
// same machine implies the user already has the keys to the kingdom.
// A future Cloudflare DO impl re-introduces auth at the transport layer
// (signed claims on Worker fetch).

import {
  DEFAULT_RETRYABLE,
  mintDeliveryKey,
  type ErrorCode,
} from '@usability-suite/schema';
import { topicMatches } from '../topic_matcher';
import { evaluateScope } from './scope';
import { MemoryBrokerStore } from './store';
import {
  systemClock,
  type BrokerConnection,
  type BrokerSnapshot,
  type BrokerStore,
  type Clock,
  type ConnectionState,
  type Envelope,
  type EventLogRow,
  type NodeRow,
  type ParticipantRow,
  type ResourceRow,
  type SessionRow,
  type To,
} from './types';

const PROTOCOL_VERSION = 1;
const RESOURCE_NAMES = new Set([
  'nodes',
  'sessions',
  'participants',
  'studies',
  'chat',
  'notes',
  'markers',
  'transcription',
  'analysis',
  'devices',
  'captureBindings',
  'recordings',
  'files',
  'events',
]);

// Feature flags surfaced in node.register.response.features. UI
// surfaces consult these instead of branching on transport so a new
// topology (DO, future Cloudflare R2-backed broker, etc.) can ship
// new capabilities without touching app code. Names follow
// `<surface>.<capability>`; the in-browser broker advertises only
// what it can honor — no real media plane, no real auth, but full
// resource CRUD + chat + notes + the per-session event log.
const BROKER_FEATURES: string[] = [
  'resources.studies',
  'resources.presets',
  'events.export',
  'chat',
  'notes',
  'markers',
  // Audio switchboard emulation. There's no real RTP plane behind
  // the broker (and the Cloudflare DO that re-uses it), but the
  // matrix RPCs are honored against in-memory state so the
  // Switchboard panel renders, mutates, and broadcasts changes the
  // same way it would against switchd.
  'audio.matrix',
];

// Built-in audio preset registry. Mirrors the `BUILTIN_PRESETS` list
// in apps/observer/src/panels/SwitchboardPanel.tsx and the C++
// `audio_preset_service.cpp` table. Surfaced via audio.matrix.get
// so the Presets view has rows without a live switchd. Keep all
// three lists in sync when adding a preset.
const BROKER_AUDIO_PRESETS: Array<{
  presetId: string;
  label: string;
  description: string;
}> = [
  {
    presetId: 'builtin:observer-gallery',
    label: 'Observer gallery',
    description:
      'Every participant → every observer. Observers hear each other; participants don’t hear observers.',
  },
  {
    presetId: 'builtin:facilitator-intercom',
    label: 'Facilitator intercom',
    description:
      'Every participant ↔ facilitator, every observer → facilitator (listen-only).',
  },
  {
    presetId: 'builtin:solo',
    label: 'Solo',
    description: 'Facilitator hears one selected participant only.',
  },
];

// Mint a user-id slug when the client didn't offer one. Same shape
// as `helmUserId()` mints on the page side — we duplicate the
// alphabet here so the broker doesn't depend on the identity
// module, which would create a circular import (identity.ts lives
// outside the broker subtree).
const USERID_ADJ = ['amber', 'azure', 'brisk', 'calm', 'crisp', 'eager'];
const USERID_NOUN = ['otter', 'falcon', 'fox', 'lynx', 'magpie', 'raven'];
const USERID_NANO = 'abcdefghijkmnpqrstuvwxyz23456789';
function mintBrokerUserId(): string {
  const adj = USERID_ADJ[Math.floor(Math.random() * USERID_ADJ.length)]!;
  const noun = USERID_NOUN[Math.floor(Math.random() * USERID_NOUN.length)]!;
  let nano = '';
  for (let i = 0; i < 4; i++) {
    nano += USERID_NANO[Math.floor(Math.random() * USERID_NANO.length)];
  }
  return `${adj}-${noun}-${nano}`;
}

export interface BrokerOptions {
  store?: BrokerStore;
  clock?: Clock;
  /** Receives broker-internal warnings; default is console.warn. */
  log?: (msg: string, detail?: unknown) => void;
}

// Writable resource families served by resource.* dispatch. Read-only
// resources (sessions/nodes/participants in flight, file-backed
// presets) remain on state.get — see PROTOCOL.md §9b for the split.
const WRITABLE_RESOURCES = new Set(['studies']);

// Internal cache row for `resource.*` dispatch. Distinct from the
// public `ResourceEntry` (broker/types.ts) the BrokerStore hooks see:
// the broker carries derived state (summary, insertionOrder) here
// that the store doesn't need to persist.
interface ResourceEntry {
  body: unknown;
  rev: string;
  summary: Record<string, unknown>;
  insertionOrder: number;
}

interface MintedToken {
  token: string;
  audience: 'files' | 'export';
  scope: Record<string, unknown>;
  expiresAtMs: number;
  mintedBy: string; // connection id; revoked on disconnect
}

// Audio switchboard emulation rows. Mirror schema/messages/audio.ts
// but stay loose with `Record<string, unknown>` for the payload pass-
// through so future schema additions don't require broker edits.
interface AudioNodeRow {
  audioNodeId: string;
  hostNodeId: string;
  sessionId?: string;
  participantRoleId?: string;
  kind: string;
  direction: string;
  label: string;
  [extra: string]: unknown;
}

interface AudioEdgeRow {
  fromAudioNodeId: string;
  toAudioNodeId: string;
  gainDb: number;
  enabled: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

interface AudioPresetRow {
  presetId: string;
  label: string;
  description?: string;
  builtin: boolean;
  edges: AudioEdgeRow[];
}

function audioEdgeKey(from: string, to: string): string {
  return from + '|' + to;
}

/**
 * TTL on the broker's delivery-key dedup window. Per PROTOCOL.md §5a:
 * a retry on the same `(from.nodeId, type, key)` inside this window
 * is recorded for audit (with `attempt > 0`) but does not re-apply
 * side effects.
 */
const DELIVERY_KEY_TTL_MS = 30_000;

export class InMemoryBroker {
  private readonly connections = new Map<string, ConnectionState>();
  private readonly store: BrokerStore;
  private readonly clock: Clock;
  private readonly log: (msg: string, detail?: unknown) => void;
  private eventSeq = 0;
  /**
   * Idempotency cache for the delivery-key tuple
   * `(from.nodeId, type, key)`. Value is the wall-clock ms when the
   * entry was inserted; a getter prunes stale rows on every lookup.
   * See PROTOCOL.md §5a "Delivery keys".
   */
  private readonly deliveryKeyCache = new Map<string, number>();
  // resource family -> id -> row. Doc storage backing resource.*.
  // localStorage persistence comes in a follow-up; today the broker
  // is the sole copy until the SharedWorker process exits.
  private readonly resources = new Map<string, Map<string, ResourceEntry>>();
  private revSeq = 0;
  private resourceInsertionSeq = 0;
  // token.mint outstanding tokens, keyed by token string.
  // mintedBy=connection id is the revocation key — cleared on disconnect.
  private readonly tokens = new Map<string, MintedToken>();
  private tokenSeq = 0;
  // Working state — kept hot so the broker doesn't re-derive on every
  // route. Snapshotted to the store after each routed envelope.
  private sessions = new Map<string, SessionRow>();
  private participants = new Map<string, ParticipantRow>(); // key: `${sessionId} ${participantRoleId}`
  private nodes = new Map<string, NodeRow>();
  // Audio switchboard emulation. There's no RTP plane behind the
  // broker, but matrix state is tracked here so the Switchboard panel
  // sees a coherent view: nodes from `audio.node` advertisements,
  // edges from facilitator writes, presets from BROKER_AUDIO_PRESETS
  // plus any `audio.preset.save`d locals. Bumped on every mutation;
  // re-broadcast as `audio.matrix.changed`.
  private audioNodes = new Map<string, AudioNodeRow>();
  private audioEdges = new Map<string, AudioEdgeRow>();
  private audioPresets = new Map<string, AudioPresetRow>();
  private audioRev = 0;

  constructor(opts: BrokerOptions = {}) {
    this.store = opts.store ?? new MemoryBrokerStore();
    this.clock = opts.clock ?? systemClock;
    this.log = opts.log ?? ((m, d) => console.warn('[broker]', m, d));
    for (const p of BROKER_AUDIO_PRESETS) {
      this.audioPresets.set(p.presetId, {
        presetId: p.presetId,
        label: p.label,
        description: p.description,
        builtin: true,
        edges: [],
      });
    }
    this.hydrate();
  }

  // -----------------------------------------------------------------
  // Public surface (called by the SharedWorker bridge or by tests).
  // -----------------------------------------------------------------

  /** Register a new connection. Returns the ConnectionState handle. */
  connect(conn: BrokerConnection): ConnectionState {
    const state: ConnectionState = {
      id: conn.id,
      conn,
      registered: false,
      nodeId: '',
      role: 'observer',
      kind: 'ui',
      subs: new Set(),
    };
    this.connections.set(conn.id, state);
    return state;
  }

  /** Drop a connection (peer disconnected or broker closed it). */
  disconnect(id: string): void {
    const state = this.connections.get(id);
    if (!state) return;
    // Release any participant binding owned by this connection.
    if (state.participantRoleId && state.sessionId) {
      const key = pKey(state.sessionId, state.participantRoleId);
      const p = this.participants.get(key);
      if (p) {
        p.state = 'disconnected';
        delete p.participantId;
        delete p.agentNodeId;
        this.broadcastParticipantStatus(p);
      }
    }
    // Drop the node row entirely. The in-browser broker has no
    // reconnect-with-same-id semantics worth preserving; if a tab
    // closes and reopens, the new tab gets a fresh nodeId and
    // registers anew, and the old row would just hang around
    // showing 'disconnected' forever otherwise. (controllerd
    // historically keeps a tombstone row so admin tooling can
    // see who *was* attached; the in-browser broker isn't that
    // surface.)
    if (state.nodeId) {
      this.nodes.delete(state.nodeId);
    }
    // Revoke any HTTP byte-pipe tokens minted by this connection.
    // Disconnect-equals-revocation keeps the lifecycle simple: a
    // dropped tab cannot continue serving downloads via a stale URL.
    for (const [tok, entry] of this.tokens) {
      if (entry.mintedBy === id) this.tokens.delete(tok);
    }
    this.connections.delete(id);
    this.persist();
  }

  /** Handle one inbound envelope from a connection. */
  onMessage(id: string, env: Envelope): void {
    const state = this.connections.get(id);
    if (!state) {
      this.log('message for unknown connection', id);
      return;
    }
    try {
      this.dispatch(state, env);
    } catch (err) {
      this.log('handler threw', { err, type: env.type });
      this.sendError(state, env, 'internal_error', String(err));
    }
  }

  /** Test/inspection: current connection map. */
  connectionCount(): number {
    return this.connections.size;
  }

  // -----------------------------------------------------------------
  // Dispatch — mirrors controller_ws_dispatch.cpp::on_ws_message.
  // -----------------------------------------------------------------

  private dispatch(state: ConnectionState, env: Envelope): void {
    if (!state.registered && env.type !== 'node.register') {
      this.sendError(
        state,
        env,
        'not_registered',
        'node.register required before ' + env.type,
      );
      return;
    }

    if (this.handleControllerMessage(state, env)) return;
    // Anything else is a published event — route it.
    this.routeEvent(env, state);
  }

  /**
   * Returns true if the envelope was a controller-bound RPC and was
   * handled here (no further fan-out needed beyond what the handler
   * itself emitted).
   */
  private handleControllerMessage(state: ConnectionState, env: Envelope): boolean {
    switch (env.type) {
      case 'node.register':
        this.handleRegister(state, env);
        return true;
      case 'subscribe':
        this.handleSubscribe(state, env);
        return true;
      case 'unsubscribe':
        this.handleUnsubscribe(state, env);
        return true;
      case 'state.get':
        this.handleStateGet(state, env);
        return true;
      case 'resource.list':
        this.handleResourceList(state, env);
        return true;
      case 'resource.get':
        this.handleResourceGet(state, env);
        return true;
      case 'resource.put':
        this.handleResourcePut(state, env);
        return true;
      case 'resource.delete':
        this.handleResourceDelete(state, env);
        return true;
      case 'token.mint':
        this.handleTokenMint(state, env);
        return true;
      case 'session.start':
        this.handleSessionStart(env);
        // session.start is also a routable event — fall through.
        this.routeEvent(env, state);
        return true;
      case 'session.pause':
        this.handleSessionPause(env);
        this.routeEvent(env, state);
        return true;
      case 'session.stop':
        this.handleSessionStop(env);
        this.routeEvent(env, state);
        return true;
      case 'participant.claim':
        this.handleParticipantClaim(state, env);
        return true;
      case 'participant.release':
        this.handleParticipantRelease(state, env);
        return true;
      case 'participant.assign':
        this.handleParticipantAssign(state, env);
        return true;
      case 'node.heartbeat': {
        const n = this.nodes.get(state.nodeId);
        if (n) n.lastSeen = this.clock.nowIso();
        return true;
      }
      case 'chat.send':
        this.handleChatSend(state, env);
        return true;
      case 'audio.matrix.get':
        this.handleAudioMatrixGet(state, env);
        return true;
      case 'audio.edge.set':
        this.handleAudioEdgeSet(state, env);
        return true;
      case 'audio.edge.clear':
        this.handleAudioEdgeClear(state, env);
        return true;
      case 'audio.preset.apply':
        this.handleAudioPresetApply(state, env);
        return true;
      case 'audio.preset.save':
        this.handleAudioPresetSave(state, env);
        return true;
      case 'audio.preset.delete':
        this.handleAudioPresetDelete(state, env);
        return true;
      case 'audio.node':
        // mediad advertisement. Upsert the node row, then fall
        // through to routeEvent so subscribers still see the raw
        // envelope.
        this.handleAudioNode(env);
        return false;
      case 'audio.node.gone':
        this.handleAudioNodeGone(env);
        return false;
      default:
        return false;
    }
  }

  // -----------------------------------------------------------------
  // node.register
  // -----------------------------------------------------------------

  private handleRegister(state: ConnectionState, env: Envelope): void {
    const payload = (env.payload as Record<string, unknown>) ?? {};
    state.registered = true;
    state.nodeId = env.from.nodeId;
    state.role = env.from.role;
    if (env.from.sessionId) state.sessionId = env.from.sessionId;
    state.kind = typeof payload.kind === 'string' ? payload.kind : 'ui';
    // Persist the stable user-id the client offered, or mint one if
    // omitted. Returned in the response so the client can adopt +
    // localStorage-persist. From here on we trust `from.userId` on
    // routed envelopes from this connection — the broker is
    // same-origin local-trust; the controllerd / DO mirror this
    // shape but additionally verify the value matches what the
    // connection registered.
    if (typeof payload.userId === 'string' && payload.userId.length > 0) {
      state.userId = payload.userId;
    } else if (typeof env.from.userId === 'string' && env.from.userId.length > 0) {
      state.userId = env.from.userId;
    } else {
      state.userId = mintBrokerUserId();
    }
    if (typeof payload.displayName === 'string' && payload.displayName.length > 0) {
      state.displayName = payload.displayName;
    } else if (
      typeof env.from.displayName === 'string' &&
      env.from.displayName.length > 0
    ) {
      state.displayName = env.from.displayName;
    }

    // Upsert the node row so state.get('nodes') reflects this tab.
    this.nodes.set(state.nodeId, {
      nodeId: state.nodeId,
      role: state.role,
      kind: state.kind,
      state: 'connected',
      lastSeen: this.clock.nowIso(),
      ...(state.sessionId ? { sessionId: state.sessionId } : {}),
    });

    this.sendResponse(state, env, 'node.register.response', {
      ok: true,
      status: 'ok',
      protocolVersion: PROTOCOL_VERSION,
      serverTime: this.clock.nowIso(),
      userId: state.userId,
      // Feature flags the in-browser broker can honor. The DO and
      // controllerd advertise a different set; UI surfaces consult
      // these flags rather than branching on transport. Names
      // follow `<surface>.<capability>`.
      features: BROKER_FEATURES,
    });
    this.persist();
  }

  // -----------------------------------------------------------------
  // subscribe / unsubscribe
  // -----------------------------------------------------------------

  private handleSubscribe(state: ConnectionState, env: Envelope): void {
    const topics = readTopics(env.payload);
    const accepted: string[] = [];
    for (const t of topics) {
      state.subs.add(t);
      accepted.push(t);
    }
    this.sendResponse(state, env, 'subscribe.response', {
      ok: true,
      accepted,
      rejected: [],
    });
  }

  private handleUnsubscribe(state: ConnectionState, env: Envelope): void {
    const topics = readTopics(env.payload);
    for (const t of topics) state.subs.delete(t);
    this.sendResponse(state, env, 'unsubscribe.response', { ok: true });
  }

  // -----------------------------------------------------------------
  // state.get
  // -----------------------------------------------------------------

  private handleStateGet(state: ConnectionState, env: Envelope): void {
    const payload =
      (env.payload as { resource?: string; filter?: Record<string, unknown> }) ?? {};
    const resource = payload.resource ?? '';
    if (!RESOURCE_NAMES.has(resource)) {
      this.sendError(state, env, 'unknown_resource', resource);
      return;
    }
    const filter = payload.filter ?? {};
    const items = this.snapshotItems(resource, filter);
    this.sendResponse(state, env, 'state.snapshot', {
      resource,
      items,
      cursor: null,
    });
  }

  private snapshotItems(resource: string, filter: Record<string, unknown>): unknown[] {
    switch (resource) {
      case 'sessions': {
        const wantState =
          typeof filter.state === 'string' ? (filter.state as string) : null;
        const rows = [...this.sessions.values()].filter((s) =>
          wantState ? s.state === wantState : true,
        );
        return rows.map(sessionToWire);
      }
      case 'participants': {
        const sid = typeof filter.sessionId === 'string' ? filter.sessionId : null;
        return [...this.participants.values()].filter((p) =>
          sid ? p.sessionId === sid : true,
        );
      }
      case 'nodes':
        return [...this.nodes.values()];
      case 'events':
        return this.eventsSnapshot(filter);
      case 'studies': {
        // Project the writable resource table the way controllerd's
        // state_service.cpp does — id / name / description / payload —
        // so a dashboard `useSnapshot('studies')` sees the same shape
        // against the broker as it does against controllerd.
        const table = this.resources.get('studies');
        if (!table) return [];
        return [...table.entries()].map(([id, row]) => {
          const body =
            (row.body as {
              name?: string;
              description?: string;
              payload?: unknown;
            }) ?? {};
          return {
            id,
            name: body.name ?? id,
            description: body.description ?? '',
            payload: body.payload ?? {},
            rev: row.rev,
          };
        });
      }
      default:
        // Resources the shim doesn't model: return empty list, never
        // an error, so the UI panels render their empty states rather
        // than surfacing a protocol error in IPC mode.
        return [];
    }
  }

  private eventsSnapshot(filter: Record<string, unknown>): EventLogRow[] {
    // We don't keep the full event log hot in the broker — pull it
    // from the store when asked.
    const all =
      this.store instanceof MemoryBrokerStore
        ? (this.store as MemoryBrokerStore).allEvents()
        : [];
    const sid = typeof filter.sessionId === 'string' ? filter.sessionId : null;
    const kind = typeof filter.kind === 'string' ? filter.kind : null;
    return all.filter((e) => {
      if (sid && e.sessionId !== sid) return false;
      if (kind && e.kind !== kind) return false;
      return true;
    });
  }

  // -----------------------------------------------------------------
  // session lifecycle
  // -----------------------------------------------------------------

  private handleSessionStart(env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const sessionId = typeof p.sessionId === 'string' ? p.sessionId : '';
    if (!sessionId) return;
    const existing = this.sessions.get(sessionId);
    const studyId =
      typeof p.studyId === 'string' ? p.studyId : (existing?.studyId ?? 'ad-hoc');
    const startedAt = typeof p.startedAt === 'string' ? p.startedAt : this.clock.nowIso();
    const wireRoles = Array.isArray(p.participantRoles) ? p.participantRoles : [];
    const participantRoles = wireRoles
      .map(
        (r) =>
          r as {
            participantRoleId?: string;
            displayName?: string;
            assignedName?: string;
          },
      )
      .filter((r) => typeof r.participantRoleId === 'string')
      .map((r) => ({
        participantRoleId: r.participantRoleId as string,
        displayName: r.displayName ?? (r.participantRoleId as string),
        ...(typeof r.assignedName === 'string' && r.assignedName.length > 0
          ? { assignedName: r.assignedName }
          : {}),
      }));
    const row: SessionRow = {
      sessionId,
      studyId,
      state: 'active',
      startedAt,
      participantRoles,
      payload: p,
    };
    this.sessions.set(sessionId, row);
    // Seed participant rows so state.get('participants') has something
    // to return even before any tab claims a slot.
    for (const r of participantRoles) {
      const key = pKey(sessionId, r.participantRoleId);
      if (!this.participants.has(key)) {
        this.participants.set(key, {
          sessionId,
          participantRoleId: r.participantRoleId,
          state: 'open',
        });
      }
    }
  }

  private handleSessionPause(env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const sessionId = typeof p.sessionId === 'string' ? p.sessionId : '';
    if (!sessionId) return;
    const row = this.sessions.get(sessionId);
    if (!row) return;
    row.state = p.paused === false ? 'active' : 'paused';
  }

  private handleSessionStop(env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const sessionId = typeof p.sessionId === 'string' ? p.sessionId : '';
    if (!sessionId) return;
    const row = this.sessions.get(sessionId);
    if (!row) return;
    row.state = 'stopped';
    row.stoppedAt = typeof p.stoppedAt === 'string' ? p.stoppedAt : this.clock.nowIso();
  }

  // -----------------------------------------------------------------
  // chat.send → chat.message
  // -----------------------------------------------------------------
  //
  // Controllerd's contract (schema/messages/chat.ts):
  //   - Client sends chat.send { message, clientMessageId? }.
  //   - Server persists, mints an id + senderName, fans out as
  //     chat.message which every subscriber — INCLUDING the sender —
  //     receives.
  //
  // The broker mirrors that shape: validates payload, builds a
  // chat.message envelope from the sender's connection state, and
  // emits it via routeEvent. Routes self-back by using a synthetic
  // "broker" sender so routeEvent's self-skip doesn't suppress the
  // echo. Also acks the original chat.send so the client's RPC
  // contract is honored.

  private handleChatSend(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    // Accept both `message` (schema-correct) and `text` (legacy
    // shim from earlier UI code) so older clients keep working
    // while the page side migrates.
    const message =
      typeof p.message === 'string' && p.message.length > 0
        ? p.message
        : typeof p.text === 'string' && p.text.length > 0
          ? p.text
          : '';
    if (!message) {
      this.sendError(state, env, 'bad_payload', 'message required');
      return;
    }
    // Session-scoped when the envelope's `from.sessionId` carries
    // one (typical observer/facilitator path once a run is live).
    // Falls back to the broker-wide chat channel when absent, which
    // is what IPC mode looks like before any session.start has
    // landed — the schema doesn't strictly require a sessionId on
    // chat.send and dropping it would just make the kiosk's
    // pre-claim chat surface die silently.
    const sessionId =
      (typeof env.from.sessionId === 'string' && env.from.sessionId) ||
      state.sessionId ||
      '';
    const id =
      'chat-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
    const senderName = state.displayName || state.userId || state.nodeId;
    const senderRole: 'facilitator' | 'observer' | 'participant-ui' | 'analysis' =
      state.role === 'facilitator' ||
      state.role === 'observer' ||
      state.role === 'participant-ui' ||
      state.role === 'analysis'
        ? (state.role as 'facilitator' | 'observer' | 'participant-ui' | 'analysis')
        : 'observer';
    // Ack the RPC. The client's pending Promise resolves on this.
    this.sendResponse(state, env, 'chat.send.response', {
      id,
      accepted: true,
    });
    // Fan out chat.message to subscribers. Use a synthetic broker
    // sender so routeEvent's self-skip lets the original sender see
    // the echo — controllerd does this implicitly by being a
    // separate node; the broker has to be explicit.
    const messageEnv: Envelope = {
      v: PROTOCOL_VERSION,
      key: mintDeliveryKey(),
      type: 'chat.message',
      from: {
        nodeId: 'broker',
        role: 'admin',
        ...(sessionId ? { sessionId } : {}),
        ...(state.userId ? { userId: state.userId } : {}),
        ...(state.displayName ? { displayName: state.displayName } : {}),
      },
      payload: {
        id,
        senderId: state.userId || state.nodeId,
        senderName,
        senderRole,
        message,
        timestamp: this.clock.nowIso(),
        ...(env.tAgent !== undefined ? { tAgent: env.tAgent } : {}),
      },
    };
    const synthetic: ConnectionState = {
      id: '__broker_chat__',
      conn: noopConn(),
      registered: true,
      nodeId: 'broker',
      role: 'admin',
      kind: 'broker',
      subs: new Set(),
    };
    this.routeEvent(messageEnv, synthetic);
  }

  // -----------------------------------------------------------------
  // participant binding
  // -----------------------------------------------------------------

  private handleParticipantClaim(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const sessionId = typeof p.sessionId === 'string' ? p.sessionId : '';
    const participantRoleId =
      typeof p.participantRoleId === 'string' ? p.participantRoleId : '';
    if (!sessionId || !participantRoleId) {
      this.sendError(
        state,
        env,
        'bad_payload',
        'sessionId and participantRoleId required',
      );
      return;
    }
    const session = this.sessions.get(sessionId);
    if (!session || session.state !== 'active') {
      this.sendResponse(state, env, 'participant.claim.response', {
        ok: false,
        reason: 'session-not-active',
      });
      return;
    }
    const key = pKey(sessionId, participantRoleId);
    let row = this.participants.get(key);
    if (!row) {
      this.sendResponse(state, env, 'participant.claim.response', {
        ok: false,
        reason: 'unknown-role',
      });
      return;
    }
    if (
      row.state === 'claimed' &&
      row.participantId &&
      row.participantId !== state.nodeId
    ) {
      this.sendResponse(state, env, 'participant.claim.response', {
        ok: false,
        reason: 'role-already-claimed',
      });
      return;
    }
    // Bind. The participant id we hand back is the connection's nodeId
    // so that other tabs' `to.scope = participant` routes resolve.
    const participantId = state.nodeId;
    row = {
      sessionId,
      participantRoleId,
      state: 'claimed',
      participantId,
    };
    this.participants.set(key, row);
    state.participantRoleId = participantRoleId;
    state.participantId = participantId;
    state.sessionId = sessionId;

    this.sendResponse(state, env, 'participant.claim.response', {
      ok: true,
      participant: {
        sessionId,
        participantRoleId,
        participantId,
      },
    });
    this.broadcastParticipantStatus(row);
  }

  private handleParticipantRelease(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const sessionId = typeof p.sessionId === 'string' ? p.sessionId : '';
    const participantRoleId =
      typeof p.participantRoleId === 'string' ? p.participantRoleId : '';
    if (!sessionId || !participantRoleId) {
      this.sendError(
        state,
        env,
        'bad_payload',
        'sessionId and participantRoleId required',
      );
      return;
    }
    const key = pKey(sessionId, participantRoleId);
    const row = this.participants.get(key);
    if (row && row.participantId === state.nodeId) {
      const next: ParticipantRow = {
        sessionId,
        participantRoleId,
        state: 'open',
      };
      this.participants.set(key, next);
      delete state.participantRoleId;
      delete state.participantId;
      this.broadcastParticipantStatus(next);
    }
    this.sendResponse(state, env, 'participant.release.response', { ok: true });
  }

  private handleParticipantAssign(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const sessionId = typeof p.sessionId === 'string' ? p.sessionId : '';
    const participantRoleId =
      typeof p.participantRoleId === 'string' ? p.participantRoleId : '';
    const agentNodeId = typeof p.agentNodeId === 'string' ? p.agentNodeId : '';
    if (!sessionId || !participantRoleId || !agentNodeId) {
      this.sendError(state, env, 'bad_payload', 'missing required fields');
      return;
    }
    const key = pKey(sessionId, participantRoleId);
    const row: ParticipantRow = {
      sessionId,
      participantRoleId,
      state: 'assigned',
      agentNodeId,
    };
    this.participants.set(key, row);
    this.sendResponse(state, env, 'participant.assign.response', {
      ok: true,
      participant: { sessionId, participantRoleId, participantId: agentNodeId },
    });
    this.broadcastParticipantStatus(row);
  }

  // -----------------------------------------------------------------
  // audio switchboard — emulated against in-memory state. No real
  // RTP plane; just enough state for the dashboard panel to render,
  // mutate, and watch its own changes round-trip. Mirrors the C++
  // `audio_matrix_service` / `audio_preset_service` surface.
  // -----------------------------------------------------------------

  private audioMatrixSnapshot(): Record<string, unknown> {
    const presets = [...this.audioPresets.values()].map((p) => ({
      presetId: p.presetId,
      label: p.label,
      ...(p.description !== undefined ? { description: p.description } : {}),
      builtin: p.builtin,
      edgeCount: p.edges.length,
    }));
    return {
      nodes: [...this.audioNodes.values()],
      edges: [...this.audioEdges.values()],
      rev: this.audioRev,
      presets,
    };
  }

  private broadcastAudioMatrixChanged(
    actor: ConnectionState,
    change: 'edge.set' | 'edge.clear' | 'preset.apply' | 'mute-all' | 'node-changed',
    extra: Record<string, unknown>,
  ): void {
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      key: mintDeliveryKey(),
      type: 'audio.matrix.changed',
      from: { nodeId: 'broker', role: 'admin' },
      payload: {
        rev: this.audioRev,
        change,
        ...(actor.userId ? { actor: actor.userId } : {}),
        ...extra,
      },
    };
    const synthetic: ConnectionState = {
      id: '__broker_audio__',
      conn: noopConn(),
      registered: true,
      nodeId: 'broker',
      role: 'admin',
      kind: 'broker',
      subs: new Set(),
    };
    this.routeEvent(env, synthetic);
  }

  private handleAudioMatrixGet(state: ConnectionState, env: Envelope): void {
    this.sendResponse(
      state,
      env,
      'audio.matrix.get.response',
      this.audioMatrixSnapshot(),
    );
  }

  private handleAudioEdgeSet(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const from = typeof p.fromAudioNodeId === 'string' ? p.fromAudioNodeId : '';
    const to = typeof p.toAudioNodeId === 'string' ? p.toAudioNodeId : '';
    if (!from || !to) {
      this.sendError(state, env, 'bad_payload', 'fromAudioNodeId/toAudioNodeId required');
      return;
    }
    if (from === to) {
      this.sendError(state, env, 'bad_payload', 'self-loop edges are not allowed');
      return;
    }
    const key = audioEdgeKey(from, to);
    const existing = this.audioEdges.get(key);
    const gainDb = typeof p.gainDb === 'number' ? p.gainDb : (existing?.gainDb ?? 0);
    const enabled =
      typeof p.enabled === 'boolean' ? p.enabled : (existing?.enabled ?? true);
    const edge: AudioEdgeRow = {
      fromAudioNodeId: from,
      toAudioNodeId: to,
      gainDb,
      enabled,
      updatedAt: this.clock.nowIso(),
      ...(state.userId ? { updatedBy: state.userId } : {}),
    };
    this.audioEdges.set(key, edge);
    this.audioRev += 1;
    this.sendResponse(state, env, 'audio.edge.set.response', {
      ok: true,
      rev: this.audioRev,
      edge,
    });
    this.broadcastAudioMatrixChanged(state, 'edge.set', { edges: [edge] });
  }

  private handleAudioEdgeClear(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const from = typeof p.fromAudioNodeId === 'string' ? p.fromAudioNodeId : '';
    const to = typeof p.toAudioNodeId === 'string' ? p.toAudioNodeId : '';
    if (!from || !to) {
      this.sendError(state, env, 'bad_payload', 'fromAudioNodeId/toAudioNodeId required');
      return;
    }
    const key = audioEdgeKey(from, to);
    const removed = this.audioEdges.delete(key);
    if (removed) this.audioRev += 1;
    this.sendResponse(state, env, 'audio.edge.clear.response', {
      ok: true,
      rev: this.audioRev,
    });
    if (removed) {
      this.broadcastAudioMatrixChanged(state, 'edge.clear', {
        removedEdges: [{ fromAudioNodeId: from, toAudioNodeId: to }],
      });
    }
  }

  private handleAudioPresetApply(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const presetId = typeof p.presetId === 'string' ? p.presetId : '';
    if (!presetId) {
      this.sendError(state, env, 'bad_payload', 'presetId required');
      return;
    }
    const replace = p.replace !== false; // default true per schema
    const inline = Array.isArray(p.edges) ? (p.edges as unknown[]) : undefined;
    const persisted = this.audioPresets.get(presetId);
    // Built-in presets ship as labels only; without a live switchd
    // there's no geometry behind them. Apply the inline edge list
    // when the dashboard supplied one; otherwise apply the persisted
    // edges (locals) or no-op for built-ins. Either way, the rev
    // bumps and the broadcast fires so the panel sees its own click.
    const sourceEdges: AudioEdgeRow[] = [];
    if (inline) {
      for (const raw of inline) {
        if (!raw || typeof raw !== 'object') continue;
        const e = raw as Record<string, unknown>;
        if (
          typeof e.fromAudioNodeId !== 'string' ||
          typeof e.toAudioNodeId !== 'string'
        ) {
          continue;
        }
        sourceEdges.push({
          fromAudioNodeId: e.fromAudioNodeId,
          toAudioNodeId: e.toAudioNodeId,
          gainDb: typeof e.gainDb === 'number' ? e.gainDb : 0,
          enabled: e.enabled !== false,
          updatedAt: this.clock.nowIso(),
          ...(state.userId ? { updatedBy: state.userId } : {}),
        });
      }
    } else if (persisted && !persisted.builtin) {
      for (const e of persisted.edges) {
        sourceEdges.push({
          ...e,
          updatedAt: this.clock.nowIso(),
          ...(state.userId ? { updatedBy: state.userId } : {}),
        });
      }
    }
    if (replace) this.audioEdges.clear();
    for (const e of sourceEdges) {
      this.audioEdges.set(audioEdgeKey(e.fromAudioNodeId, e.toAudioNodeId), e);
    }
    this.audioRev += 1;
    this.sendResponse(state, env, 'audio.preset.apply.response', {
      ok: true,
      rev: this.audioRev,
      appliedEdges: sourceEdges.length,
    });
    this.broadcastAudioMatrixChanged(state, 'preset.apply', {
      presetId,
      edges: [...this.audioEdges.values()],
    });
  }

  private handleAudioPresetSave(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const presetId = typeof p.presetId === 'string' ? p.presetId : '';
    const label = typeof p.label === 'string' ? p.label : '';
    if (!presetId || !label) {
      this.sendError(state, env, 'bad_payload', 'presetId and label required');
      return;
    }
    if (presetId.startsWith('builtin:')) {
      this.sendError(state, env, 'bad_payload', 'cannot overwrite builtin preset');
      return;
    }
    const inline = Array.isArray(p.edges) ? (p.edges as unknown[]) : [];
    const edges: AudioEdgeRow[] = [];
    for (const raw of inline) {
      if (!raw || typeof raw !== 'object') continue;
      const e = raw as Record<string, unknown>;
      if (typeof e.fromAudioNodeId !== 'string' || typeof e.toAudioNodeId !== 'string') {
        continue;
      }
      edges.push({
        fromAudioNodeId: e.fromAudioNodeId,
        toAudioNodeId: e.toAudioNodeId,
        gainDb: typeof e.gainDb === 'number' ? e.gainDb : 0,
        enabled: e.enabled !== false,
      });
    }
    this.audioPresets.set(presetId, {
      presetId,
      label,
      ...(typeof p.description === 'string' ? { description: p.description } : {}),
      builtin: false,
      edges,
    });
    this.audioRev += 1;
    this.sendResponse(state, env, 'audio.preset.save.response', {
      ok: true,
      presetId,
    });
    this.broadcastAudioMatrixChanged(state, 'node-changed', {});
  }

  private handleAudioPresetDelete(state: ConnectionState, env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const presetId = typeof p.presetId === 'string' ? p.presetId : '';
    if (!presetId) {
      this.sendError(state, env, 'bad_payload', 'presetId required');
      return;
    }
    if (presetId.startsWith('builtin:')) {
      this.sendError(state, env, 'bad_payload', 'cannot delete builtin preset');
      return;
    }
    const existing = this.audioPresets.get(presetId);
    const deleted =
      existing && !existing.builtin ? this.audioPresets.delete(presetId) : false;
    if (deleted) this.audioRev += 1;
    this.sendResponse(state, env, 'audio.preset.delete.response', {
      ok: true,
      deleted,
    });
    if (deleted) this.broadcastAudioMatrixChanged(state, 'node-changed', {});
  }

  private handleAudioNode(env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const audioNodeId = typeof p.audioNodeId === 'string' ? p.audioNodeId : '';
    const hostNodeId = typeof p.hostNodeId === 'string' ? p.hostNodeId : '';
    if (!audioNodeId || !hostNodeId) return;
    this.audioNodes.set(audioNodeId, {
      audioNodeId,
      hostNodeId,
      ...(typeof p.sessionId === 'string' ? { sessionId: p.sessionId } : {}),
      ...(typeof p.participantRoleId === 'string'
        ? { participantRoleId: p.participantRoleId }
        : {}),
      kind: typeof p.kind === 'string' ? p.kind : 'other',
      direction: typeof p.direction === 'string' ? p.direction : 'duplex',
      label: typeof p.label === 'string' ? p.label : audioNodeId,
    });
    this.audioRev += 1;
  }

  private handleAudioNodeGone(env: Envelope): void {
    const p = (env.payload as Record<string, unknown>) ?? {};
    const audioNodeId = typeof p.audioNodeId === 'string' ? p.audioNodeId : '';
    if (!audioNodeId) return;
    if (this.audioNodes.delete(audioNodeId)) this.audioRev += 1;
  }

  // -----------------------------------------------------------------
  // route_event — subscription fan-out + scope target fan-out + log.
  // -----------------------------------------------------------------

  private routeEvent(env: Envelope, sender: ConnectionState): void {
    const sessionId = pickSessionId(env);
    // Delivery-key dedup: a second arrival on the same
    // `(from.nodeId, type, key)` inside the TTL window is recorded for
    // audit but never re-applies side effects and is fanned only back
    // to the sender (so their pending receipt still flips). See
    // PROTOCOL.md §5a.
    const isRetry = env.key ? this.checkDeliveryKey(env, sender) : false;
    if (isRetry) {
      // Always echo to sender so their pending receipt flips.
      sender.conn.send(env);
      this.recordEvent(env, sender, sessionId);
      this.persist();
      return;
    }

    const targets = new Set<string>();
    const topics = [env.type];
    if (sessionId) topics.push(`session.${sessionId}.${env.type}`);
    // Resource-scoped topic synthesis (PROTOCOL.md §10):
    // resource.changed expands onto resource.<name>.changed and
    // resource.<name>.<id>.changed so subscribers can scope precisely.
    if (
      env.type === 'resource.changed' &&
      env.payload &&
      typeof env.payload === 'object'
    ) {
      const p = env.payload as { resource?: unknown; id?: unknown };
      if (typeof p.resource === 'string' && p.resource.length > 0) {
        topics.push(`resource.${p.resource}.changed`);
        if (typeof p.id === 'string' && p.id.length > 0) {
          topics.push(`resource.${p.resource}.${p.id}.changed`);
        }
      }
    }
    for (const t of topics) {
      for (const [id, state] of this.connections) {
        if (!state.registered) continue;
        for (const pattern of state.subs) {
          if (topicMatches(pattern, t)) {
            targets.add(id);
            break;
          }
        }
      }
    }
    // Scope-based routing.
    for (const id of evaluateScope(env.to, this.connections.values())) {
      targets.add(id);
    }
    // Always echo to the sender (PROTOCOL.md §5a). The sender uses
    // `(env.key, env.from.nodeId === self)` to flip their pending
    // delivery receipt to `sent`.
    targets.add(sender.id);
    for (const id of targets) {
      const target = this.connections.get(id);
      if (target) target.conn.send(env);
    }
    this.recordEvent(env, sender, sessionId);
    this.persist();
  }

  /**
   * Lookup + insert into the delivery-key cache. Returns `true` if the
   * tuple was already present (this publish is a retry). Prunes
   * expired rows on every call so the map stays bounded by the recent
   * publish rate.
   */
  private checkDeliveryKey(env: Envelope, sender: ConnectionState): boolean {
    const now = this.clock.now();
    // Cheap prune — bounded by the publish rate × TTL.
    if (this.deliveryKeyCache.size > 0) {
      const cutoff = now - DELIVERY_KEY_TTL_MS;
      for (const [k, ts] of this.deliveryKeyCache) {
        if (ts < cutoff) this.deliveryKeyCache.delete(k);
      }
    }
    const tuple = `${sender.nodeId} ${env.type} ${env.key}`;
    if (this.deliveryKeyCache.has(tuple)) return true;
    this.deliveryKeyCache.set(tuple, now);
    return false;
  }

  private broadcastParticipantStatus(row: ParticipantRow): void {
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      key: mintDeliveryKey(),
      type: 'participant.status',
      from: {
        nodeId: 'broker',
        role: 'admin',
        sessionId: row.sessionId,
      },
      payload: rowToParticipantStatus(row),
    };
    // Sender is "the broker" — synthesize a fake state so routeEvent's
    // self-skip doesn't suppress the event to anyone real.
    const synthetic: ConnectionState = {
      id: '__broker__',
      conn: noopConn(),
      registered: true,
      nodeId: 'broker',
      role: 'admin',
      kind: 'ui',
      subs: new Set(),
    };
    this.routeEvent(env, synthetic);
  }

  // -----------------------------------------------------------------
  // Persistence helpers.
  // -----------------------------------------------------------------

  private recordEvent(env: Envelope, sender: ConnectionState, sessionId: string): void {
    const row: EventLogRow = {
      id: ++this.eventSeq,
      sessionId,
      kind: env.type,
      producer: sender.nodeId,
      role: sender.role,
      recordedAt: this.clock.nowIso(),
      key: env.key,
      attempt: env.attempt ?? 0,
      payload: env.payload,
    };
    if (env.tAgent !== undefined) row.tAgent = env.tAgent;
    if (env.correlationId !== undefined) row.correlationId = env.correlationId;
    if (env.to !== undefined) row.to = env.to;
    this.store.appendEvent(row);
  }

  private persist(): void {
    const snapshot: BrokerSnapshot = {
      version: 1,
      // Nodes are inherently transient — each row corresponds to a
      // live WebSocket / MessagePort. Persisting them across a
      // SharedWorker restart just resurrects rows for tabs that
      // are no longer connected (the rehydrate marks them all
      // 'disconnected' but the NodesPanel still renders them).
      // Skip them entirely; a fresh worker starts with no nodes
      // and the next register repopulates the map.
      nodes: [],
      sessions: [...this.sessions.values()],
      participants: [...this.participants.values()],
      events: [], // events live in the store's appendEvent stream.
    };
    this.store.save(snapshot);
  }

  private hydrate(): void {
    const maybe = this.store.load();
    if (maybe && typeof (maybe as Promise<unknown>).then === 'function') {
      // Async store (Cloudflare DO impl). Fire-and-forget — late
      // hydration overwrites whatever the live broker has built up so
      // far. In-tab usage paths take the synchronous branch below.
      void (maybe as Promise<unknown>).then((loaded) =>
        this.applyHydrated(loaded as ReturnType<BrokerStore['load']>),
      );
    } else {
      this.applyHydrated(maybe);
    }
    // Resources are loaded through a parallel hook — stores that
    // don't persist them (the in-browser broker) leave it undefined
    // and skip the rehydrate.
    const resources = this.store.loadResources?.();
    if (!resources) return;
    if (typeof (resources as Promise<unknown>).then === 'function') {
      void (resources as Promise<ResourceRow[]>).then((rows) =>
        this.applyHydratedResources(rows),
      );
    } else {
      this.applyHydratedResources(resources as ResourceRow[]);
    }
  }

  private applyHydrated(loaded: ReturnType<BrokerStore['load']> | null): void {
    if (!loaded || (loaded as Promise<unknown>).then) return;
    const snap = loaded as BrokerSnapshot;
    for (const n of snap.nodes) {
      // Restored connections are stale by definition — mark all
      // disconnected; live connect()s will overwrite.
      this.nodes.set(n.nodeId, { ...n, state: 'disconnected' });
    }
    for (const s of snap.sessions) this.sessions.set(s.sessionId, s);
    for (const p of snap.participants) {
      this.participants.set(pKey(p.sessionId, p.participantRoleId), p);
    }
  }

  private applyHydratedResources(rows: ResourceRow[]): void {
    // Rev sequence has to outrun any rev we already wrote, otherwise
    // the next `resource.put` would mint a colliding token. Parse
    // each row's rev as an integer and bump revSeq past the max.
    let maxRev = this.revSeq;
    for (const row of rows) {
      const table = this.tableFor(row.resource);
      table.set(row.id, {
        body: row.body,
        rev: row.rev,
        summary: projectSummary(row.resource, row.body),
        insertionOrder: ++this.resourceInsertionSeq,
      });
      const n = Number.parseInt(row.rev, 10);
      if (Number.isFinite(n) && n > maxRev) maxRev = n;
    }
    this.revSeq = maxRev;
    // Hydration finished after the initial connect handshake in the
    // async-store case (IDB-backed SharedWorker). Re-emit a
    // resource.changed for each restored row so any already-
    // subscribed dashboard's `subscribeToStudyChanges` / studies
    // snapshot reloader picks up the late arrival without the user
    // having to refresh.
    for (const row of rows) {
      this.emitResourceChangedSynthetic(row.resource, row.id, row.rev);
    }
  }

  /**
   * Broadcast a resource.changed event without a sender connection.
   * Used after async hydration so subscribers re-fetch on cold boot
   * once the IDB load resolves.
   */
  private emitResourceChangedSynthetic(resource: string, id: string, rev: string): void {
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      key: mintDeliveryKey(),
      type: 'resource.changed',
      from: { nodeId: 'broker', role: 'admin' },
      payload: { resource, id, rev, op: 'put' },
    };
    const synthetic: ConnectionState = {
      id: '__broker_hydrate__',
      conn: noopConn(),
      registered: true,
      nodeId: 'broker',
      role: 'admin',
      kind: 'broker',
      subs: new Set(),
    };
    this.routeEvent(env, synthetic);
  }

  // -----------------------------------------------------------------
  // resource.* dispatch (PROTOCOL.md §9b)
  // -----------------------------------------------------------------

  private handleResourceList(state: ConnectionState, env: Envelope): void {
    const p =
      (env.payload as { resource?: unknown; cursor?: unknown; limit?: unknown }) ?? {};
    const resource = p.resource;
    if (typeof resource !== 'string' || !WRITABLE_RESOURCES.has(resource)) {
      this.sendError(
        state,
        env,
        'unknown_resource',
        typeof resource === 'string' ? resource : '(missing)',
      );
      return;
    }
    const table = this.resources.get(resource);
    // Stable order by insertion sequence so cursor-pagination is
    // deterministic. Real backends (controllerd / DO) order by
    // (id) or (updated_at) — the contract is "consistent across a
    // paginated walk," not a specific sort key.
    const rows = table
      ? [...table.entries()].sort((a, b) => a[1].insertionOrder - b[1].insertionOrder)
      : [];
    const limit =
      typeof p.limit === 'number' && Number.isFinite(p.limit)
        ? Math.max(1, Math.min(500, Math.floor(p.limit)))
        : 500;
    const cursor = typeof p.cursor === 'string' ? p.cursor : null;
    let startIdx = 0;
    if (cursor) {
      const decoded = parseInt(cursor, 10);
      if (Number.isFinite(decoded)) startIdx = decoded;
    }
    const slice = rows.slice(startIdx, startIdx + limit);
    const nextIdx = startIdx + slice.length;
    const hasMore = nextIdx < rows.length;
    const items = slice.map(([id, row]) => ({
      id,
      rev: row.rev,
      summary: row.summary,
    }));
    const payload: Record<string, unknown> = { resource, items, hasMore };
    if (hasMore) payload.nextCursor = String(nextIdx);
    this.sendResponse(state, env, 'resource.list.response', payload);
  }

  private handleResourceGet(state: ConnectionState, env: Envelope): void {
    const p =
      (env.payload as { resource?: unknown; id?: unknown; ifNoneMatch?: unknown }) ?? {};
    const resource = p.resource;
    const id = p.id;
    if (typeof resource !== 'string' || !WRITABLE_RESOURCES.has(resource)) {
      this.sendError(
        state,
        env,
        'unknown_resource',
        typeof resource === 'string' ? resource : '(missing)',
      );
      return;
    }
    if (typeof id !== 'string' || id.length === 0) {
      this.sendError(state, env, 'bad_payload', 'id required');
      return;
    }
    const row = this.resources.get(resource)?.get(id);
    if (!row) {
      this.sendError(state, env, 'not_found', `${resource}/${id}`);
      return;
    }
    if (typeof p.ifNoneMatch === 'string' && p.ifNoneMatch === row.rev) {
      this.sendResponse(state, env, 'resource.get.response', {
        status: 'notModified',
        rev: row.rev,
      });
      return;
    }
    this.sendResponse(state, env, 'resource.get.response', {
      status: 'ok',
      rev: row.rev,
      body: row.body,
    });
  }

  private handleResourcePut(state: ConnectionState, env: Envelope): void {
    const p =
      (env.payload as {
        resource?: unknown;
        id?: unknown;
        body?: unknown;
        ifMatch?: unknown;
      }) ?? {};
    const resource = p.resource;
    const id = p.id;
    if (typeof resource !== 'string' || !WRITABLE_RESOURCES.has(resource)) {
      this.sendError(
        state,
        env,
        'unknown_resource',
        typeof resource === 'string' ? resource : '(missing)',
      );
      return;
    }
    if (typeof id !== 'string' || id.length === 0) {
      this.sendError(state, env, 'bad_payload', 'id required');
      return;
    }
    const table = this.tableFor(resource);
    const existing = table.get(id);
    const ifMatch = typeof p.ifMatch === 'string' ? p.ifMatch : null;
    if (ifMatch !== null) {
      if (!existing) {
        this.sendError(state, env, 'not_found', `${resource}/${id}`);
        return;
      }
      if (existing.rev !== ifMatch) {
        this.sendError(
          state,
          env,
          'conflict',
          `rev mismatch: have ${existing.rev}, given ${ifMatch}`,
        );
        return;
      }
    }
    const newRev = String(++this.revSeq);
    const row: ResourceEntry = {
      body: p.body ?? null,
      rev: newRev,
      summary: projectSummary(resource, p.body),
      insertionOrder: existing?.insertionOrder ?? ++this.resourceInsertionSeq,
    };
    table.set(id, row);
    this.store.saveResource?.({ resource, id, body: row.body, rev: newRev });
    this.sendResponse(state, env, 'resource.put.response', { rev: newRev });
    this.emitResourceChanged(state, resource, id, newRev, 'put');
  }

  private handleResourceDelete(state: ConnectionState, env: Envelope): void {
    const p =
      (env.payload as { resource?: unknown; id?: unknown; ifMatch?: unknown }) ?? {};
    const resource = p.resource;
    const id = p.id;
    if (typeof resource !== 'string' || !WRITABLE_RESOURCES.has(resource)) {
      this.sendError(
        state,
        env,
        'unknown_resource',
        typeof resource === 'string' ? resource : '(missing)',
      );
      return;
    }
    if (typeof id !== 'string' || id.length === 0) {
      this.sendError(state, env, 'bad_payload', 'id required');
      return;
    }
    const table = this.resources.get(resource);
    const existing = table?.get(id);
    if (!existing) {
      this.sendError(state, env, 'not_found', `${resource}/${id}`);
      return;
    }
    if (typeof p.ifMatch === 'string' && p.ifMatch !== existing.rev) {
      this.sendError(
        state,
        env,
        'conflict',
        `rev mismatch: have ${existing.rev}, given ${p.ifMatch}`,
      );
      return;
    }
    table!.delete(id);
    this.store.deleteResource?.(resource, id);
    this.sendResponse(state, env, 'resource.delete.response', {
      success: true,
      rev: existing.rev,
    });
    this.emitResourceChanged(state, resource, id, existing.rev, 'delete');
  }

  private tableFor(resource: string): Map<string, ResourceEntry> {
    let t = this.resources.get(resource);
    if (!t) {
      t = new Map();
      this.resources.set(resource, t);
    }
    return t;
  }

  private emitResourceChanged(
    sender: ConnectionState,
    resource: string,
    id: string,
    rev: string,
    op: 'put' | 'delete',
  ): void {
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      key: mintDeliveryKey(),
      type: 'resource.changed',
      from: { nodeId: sender.nodeId, role: sender.role },
      payload: { resource, id, rev, op, actor: sender.nodeId },
    };
    // routeEvent self-skips the sender; that's correct here too —
    // the originating connection already got the .response and
    // doesn't need a redundant change event.
    this.routeEvent(env, sender);
  }

  // -----------------------------------------------------------------
  // token.mint dispatch (PROTOCOL.md §11a)
  // -----------------------------------------------------------------

  private handleTokenMint(state: ConnectionState, env: Envelope): void {
    const p =
      (env.payload as {
        audience?: unknown;
        scope?: unknown;
        ttlSec?: unknown;
      }) ?? {};
    const audience = p.audience;
    if (audience !== 'files' && audience !== 'export') {
      this.sendError(state, env, 'bad_payload', 'audience must be one of: files, export');
      return;
    }
    const ttlSec =
      typeof p.ttlSec === 'number' && Number.isFinite(p.ttlSec)
        ? Math.max(1, Math.min(3600, Math.floor(p.ttlSec)))
        : 0;
    if (ttlSec <= 0) {
      this.sendError(state, env, 'bad_payload', 'ttlSec required (1..3600)');
      return;
    }
    const scope = (p.scope && typeof p.scope === 'object' ? p.scope : {}) as Record<
      string,
      unknown
    >;
    let url = '';
    if (audience === 'files') {
      const fileId = scope.fileId;
      const recipient = scope.recipient;
      if (
        typeof fileId !== 'string' ||
        fileId.length === 0 ||
        typeof recipient !== 'string' ||
        recipient.length === 0
      ) {
        this.sendError(
          state,
          env,
          'bad_payload',
          'files audience requires scope.fileId and scope.recipient',
        );
        return;
      }
      // URL synthesized below after token is minted.
      url = `/api/files/${encodeURIComponent(fileId)}`;
    } else {
      // export: per-session when scope.sessionId is set, cross-session
      // otherwise. format is required either way.
      const sessionId = scope.sessionId;
      const format = scope.format;
      if (format !== 'jsonl' && format !== 'csv') {
        this.sendError(
          state,
          env,
          'bad_payload',
          'export audience requires scope.format in {jsonl, csv}',
        );
        return;
      }
      if (typeof sessionId === 'string' && sessionId.length > 0) {
        url = `/api/sessions/${encodeURIComponent(sessionId)}/export`;
      } else {
        url = '/api/events/export';
      }
    }
    const token = `t-${++this.tokenSeq}-${Math.random().toString(36).slice(2, 10)}`;
    const expiresAtMs = this.clock.now() + ttlSec * 1000;
    this.tokens.set(token, {
      token,
      audience,
      scope,
      expiresAtMs,
      mintedBy: state.id,
    });
    const params: string[] = [`token=${encodeURIComponent(token)}`];
    if (audience === 'export') {
      params.push(`format=${encodeURIComponent(scope.format as string)}`);
      // Cross-session filter narrowing — propagate the optional
      // filter fields onto the URL so the HTTP route can apply them
      // without re-decoding the token. The token still binds them
      // (the validator checks the URL's filter params against the
      // token's scope).
      if (
        typeof scope.sessionId !== 'string' ||
        (scope.sessionId as string).length === 0
      ) {
        for (const key of ['studyId', 'kind', 'since', 'until'] as const) {
          const v = scope[key];
          if (typeof v === 'string' && v.length > 0) {
            params.push(`${key}=${encodeURIComponent(v)}`);
          }
        }
      }
    } else {
      params.push(`recipient=${encodeURIComponent(scope.recipient as string)}`);
    }
    const fullUrl = url + '?' + params.join('&');
    this.sendResponse(state, env, 'token.mint.response', {
      token,
      expiresAt: new Date(expiresAtMs).toISOString(),
      url: fullUrl,
    });
  }

  // -----------------------------------------------------------------
  // Wire helpers.
  // -----------------------------------------------------------------

  private sendResponse(
    state: ConnectionState,
    req: Envelope,
    type: string,
    payload: unknown,
  ): void {
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      key: mintDeliveryKey(),
      type,
      from: { nodeId: 'broker', role: 'admin' },
      payload,
    };
    if (req.correlationId !== undefined) env.correlationId = req.correlationId;
    state.conn.send(env);
  }

  private sendError(
    state: ConnectionState,
    req: Envelope,
    code: ErrorCode,
    message: string,
    retryable: boolean = DEFAULT_RETRYABLE[code],
  ): void {
    const env: Envelope = {
      v: PROTOCOL_VERSION,
      key: mintDeliveryKey(),
      type: 'error',
      from: { nodeId: 'broker', role: 'admin' },
      payload: {
        code,
        message,
        retryable,
        requestType: req.type,
      },
    };
    if (req.correlationId !== undefined) env.correlationId = req.correlationId;
    state.conn.send(env);
  }

  /** Test/debug: export a session as JSON via the store. */
  exportSession(sessionId: string) {
    return this.store.exportSession(sessionId);
  }

  /**
   * Cross-session export: every event log row, optionally narrowed by
   * the same filter shape `state.get('events', filter)` accepts. Used
   * by the SW's /api/events/export route in broker mode.
   */
  exportEvents(filter: {
    studyId?: string;
    kind?: string;
    since?: string;
    until?: string;
  }) {
    const all =
      this.store instanceof MemoryBrokerStore
        ? (this.store as MemoryBrokerStore).allEvents()
        : [];
    const f = filter ?? {};
    const events = all.filter((e) => {
      if (f.kind && e.kind !== f.kind) return false;
      // studyId / since / until are not pinned on the EventLogRow
      // shape today; the broker's event log doesn't carry studyId
      // (sessions do). Fall through — these filters are honored by
      // controllerd's event log; the broker matches what it can.
      void f.studyId;
      void f.since;
      void f.until;
      return true;
    });
    return { events };
  }

  /**
   * Resolve a `token.mint`-minted opaque token back to its
   * (audience, scope) — or null if expired, never minted, or revoked
   * by the minting connection's disconnect. The Service Worker
   * fetch interceptor uses this to authorise HTTP byte-pipe fetches
   * served from the broker.
   */
  verifyToken(token: string): { audience: string; scope: unknown } | null {
    const entry = this.tokens.get(token);
    if (!entry) return null;
    if (entry.expiresAtMs <= this.clock.now()) {
      this.tokens.delete(token);
      return null;
    }
    return { audience: entry.audience, scope: entry.scope };
  }

  /** Test/debug: handle on the active store. */
  storeRef(): BrokerStore {
    return this.store;
  }
}

// ----------------------------------------------------------------------
// Helpers (pure)
// ----------------------------------------------------------------------

function pKey(sessionId: string, participantRoleId: string): string {
  return sessionId + ' ' + participantRoleId;
}

function readTopics(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') return [];
  const obj = payload as { topics?: unknown };
  if (!Array.isArray(obj.topics)) return [];
  return obj.topics.filter((t): t is string => typeof t === 'string');
}

function pickSessionId(env: Envelope): string {
  if (env.from.sessionId) return env.from.sessionId;
  if (env.payload && typeof env.payload === 'object') {
    const p = env.payload as { sessionId?: unknown };
    if (typeof p.sessionId === 'string') return p.sessionId;
  }
  if (env.to?.sessionId) return env.to.sessionId;
  return '';
}

function sessionToWire(row: SessionRow): Record<string, unknown> {
  return {
    sessionId: row.sessionId,
    studyId: row.studyId,
    state: row.state,
    status: row.state,
    ...(row.startedAt ? { startedAt: row.startedAt } : {}),
    ...(row.stoppedAt ? { stoppedAt: row.stoppedAt } : {}),
    participantRoles: row.participantRoles,
    payload: row.payload,
  };
}

function rowToParticipantStatus(row: ParticipantRow): Record<string, unknown> {
  const out: Record<string, unknown> = {
    sessionId: row.sessionId,
    participantRoleId: row.participantRoleId,
    state: row.state,
  };
  if (row.participantId) {
    out.participant = {
      sessionId: row.sessionId,
      participantRoleId: row.participantRoleId,
      participantId: row.participantId,
    };
  }
  if (row.agentNodeId) out.agentNodeId = row.agentNodeId;
  return out;
}

/**
 * Per-resource summary projection. The broker stores the full body
 * and returns this lighter shape from resource.list so a 50-study
 * dashboard doesn't pull every payload over the wire. New writable
 * resources add a branch here.
 */
function projectSummary(resource: string, body: unknown): Record<string, unknown> {
  if (resource !== 'studies') return {};
  if (!body || typeof body !== 'object') return {};
  const b = body as { name?: unknown; description?: unknown; updatedAt?: unknown };
  const summary: Record<string, unknown> = {};
  if (typeof b.name === 'string') summary.name = b.name;
  if (typeof b.description === 'string') summary.description = b.description;
  if (typeof b.updatedAt === 'string') summary.updatedAt = b.updatedAt;
  return summary;
}

function noopConn(): BrokerConnection {
  return {
    id: '__broker__',
    send: () => {
      /* noop */
    },
    close: () => {
      /* noop */
    },
  };
}

export type { To };
